"use client";

import { useMemo } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ArrowLeft } from "lucide-react";

import { CreatePostForm } from "@/components/feed/create-post-form";
import { FeedAdCard } from "@/components/feed/feed-ad-card";
import { FeedEmptyState, FeedErrorState, FeedLoadingState } from "@/components/feed/feed-states";
import { HomeGreeting } from "@/components/feed/home-greeting";
import { PostCard } from "@/components/feed/post-card";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useFeedAds } from "@/hooks/use-feed-ads";
import {
  useFeedPost,
  useFeedPosts,
  useTrackVisibleFeedPosts,
} from "@/hooks/use-feed";
import { mixFeedAds } from "@/lib/feed/ads";
import type { FeedFilter } from "@/lib/feed/types";
import { cn, getErrorMessage } from "@/lib/utils";

const FEED_FILTERS: { id: FeedFilter; label: string }[] = [
  { id: "all", label: "Összes" },
  { id: "question", label: "Szakmai kérdések" },
  { id: "poll", label: "Szavazások" },
  { id: "community", label: "Közösségi bejegyzések" },
];

function isFeedFilter(value: string | null): value is FeedFilter {
  return FEED_FILTERS.some((filter) => filter.id === value);
}

export function Feed() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const focusedPostId = searchParams.get("post");
  const requestedFilter = searchParams.get("type");
  const activeFilter = isFeedFilter(requestedFilter) ? requestedFilter : "all";
  const currentUserQuery = useCurrentUser();
  const currentUser = currentUserQuery.data;
  const currentUserId =
    currentUser?.accountStatus === "active" ? currentUser.id : null;
  const isHome = pathname === "/";
  const singlePostMode = Boolean(focusedPostId);
  const feedQuery = useFeedPosts(
    Boolean(currentUserId) && !singlePostMode,
    isHome ? "all" : activeFilter,
  );
  const focusedPostQuery = useFeedPost(
    focusedPostId,
    Boolean(currentUserId) && singlePostMode,
  );
  const posts = singlePostMode
    ? focusedPostQuery.data
      ? [focusedPostQuery.data]
      : []
    : feedQuery.posts;
  const showAds =
    Boolean(currentUserId) &&
    !singlePostMode &&
    (isHome || activeFilter === "all");
  const adsQuery = useFeedAds(showAds, "home");
  const timeline = useMemo(
    () =>
      showAds
        ? mixFeedAds(
            posts,
            adsQuery.data?.ads ?? [],
            currentUserId ?? "anon",
            adsQuery.data?.slotEvery,
          )
        : posts.map((post) => ({
            kind: "post" as const,
            key: post.id,
            post,
          })),
    [adsQuery.data, currentUserId, posts, showAds],
  );
  useTrackVisibleFeedPosts(posts, Boolean(currentUserId));
  const canCompose = Boolean(currentUserId) && !singlePostMode;
  const isLoading =
    currentUserQuery.isLoading ||
    (currentUserId &&
      (singlePostMode ? focusedPostQuery.isLoading : feedQuery.isLoading));
  const isError =
    currentUserQuery.isError ||
    (singlePostMode ? focusedPostQuery.isError : feedQuery.isError);
  const error = singlePostMode
    ? focusedPostQuery.error ?? currentUserQuery.error
    : feedQuery.error ?? currentUserQuery.error;

  return (
    <div className="mx-auto w-full max-w-[800px] min-w-0 space-y-5 overflow-x-clip">
      {singlePostMode ? (
        <div className="flex items-center justify-between gap-3">
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link href={pathname === "/" ? "/" : "/feed"}>
              <ArrowLeft className="size-4" />
              Vissza a hírfolyamhoz
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground">Keresési találat</p>
        </div>
      ) : isHome ? (
        <HomeGreeting
          fullName={currentUser?.fullName}
          enabled={Boolean(currentUserId)}
        />
      ) : pathname === "/feed" ? (
        <FeedFilters activeFilter={activeFilter} />
      ) : null}

      {isLoading ? (
        <>
          {canCompose ? <CreatePostForm hero={isHome} /> : null}
          <FeedLoadingState />
        </>
      ) : isError ? (
        <>
          <FeedErrorState
            message={getErrorMessage(error)}
            onRetry={() => {
              void currentUserQuery.refetch();
              if (singlePostMode) {
                void focusedPostQuery.refetch();
              } else {
                void feedQuery.refetch();
              }
            }}
          />
        </>
      ) : !currentUserId ? (
        <>
          <FeedErrorState
            message={
              currentUser
                ? "A hírfolyam csak jóváhagyott, aktív PraxisTárs fiókkal érhető el."
                : "A hírfolyam megtekintéséhez be kell jelentkezned."
            }
          />
        </>
      ) : (
        <>
          {canCompose ? <CreatePostForm hero={isHome} /> : null}
          {posts.length === 0 ? (
            singlePostMode ? (
              <FeedErrorState message="A keresett bejegyzés nem található, vagy nincs jogosultsága a megtekintéséhez." />
            ) : (
              <FeedEmptyState />
            )
          ) : (
            <>
              <section className="space-y-4">
                {timeline.map((item) =>
                  item.kind === "ad" ? (
                    <FeedAdCard key={item.key} ad={item.ad} />
                  ) : (
                    <PostCard
                      key={item.key}
                      post={item.post}
                      currentUserId={currentUserId}
                    />
                  ),
                )}
              </section>
              {!singlePostMode && feedQuery.hasNextPage ? (
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={feedQuery.isFetchingNextPage}
                    onClick={() => void feedQuery.fetchNextPage()}
                  >
                    {feedQuery.isFetchingNextPage
                      ? "Betöltés…"
                      : "További bejegyzések"}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

function FeedFilters({ activeFilter }: { activeFilter: FeedFilter }) {
  return (
    <nav
      aria-label="Hírfolyam szűrése"
      className="flex gap-2 overflow-x-auto pb-1"
    >
      {FEED_FILTERS.map((filter) => (
        <Link
          key={filter.id}
          href={filter.id === "all" ? "/feed" : `/feed?type=${filter.id}`}
          className={cn(
            "shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
            activeFilter === filter.id
              ? "border-primary bg-primary text-primary-foreground"
              : "border-slate-200 bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          {filter.label}
        </Link>
      ))}
    </nav>
  );
}
